import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Link2, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { appPath } from '../utils/appRouting';
import { isNativeApp } from '../utils/isNativeApp';
import {
  clearPendingGoogleCredential,
  loadPendingGoogleCredential,
  linkPendingCredentialToUser
} from '../auth/googleAuth';

export default function LinkGoogleAccount() {
  const navigate = useNavigate();
  const { login, currentUser, refreshUserProfile } = useAuth();

  const pending = useMemo(() => loadPendingGoogleCredential(), []);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: pending?.email || '',
    password: ''
  });

  const isApp = isNativeApp();

  useEffect(() => {
    if (!pending?.email) {
      toast.info('Nessuna richiesta di collegamento trovata');
      navigate(appPath('/login'), { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Se l'utente è già loggato e l'email coincide, prova a collegare subito.
    const normalizedPending = String(pending?.email || '').trim().toLowerCase();
    const normalizedCurrent = String(currentUser?.email || '').trim().toLowerCase();
    if (pending?.email && currentUser && normalizedPending && normalizedPending === normalizedCurrent) {
      (async () => {
        try {
          setLoading(true);
          await linkPendingCredentialToUser(currentUser);
          await refreshUserProfile?.();
          toast.success('Account Google collegato');
          navigate(appPath('/dashboard'), { replace: true });
        } catch (e) {
          console.error('Auto-link error:', e);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [currentUser, navigate, pending?.email, refreshUserProfile]);

  const handleCancel = () => {
    clearPendingGoogleCredential();
    navigate(appPath('/login'), { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const normalizedPending = String(pending?.email || '').trim().toLowerCase();
    const normalizedEmail = String(form.email || '').trim().toLowerCase();

    if (!normalizedPending || !normalizedEmail || normalizedPending !== normalizedEmail) {
      toast.error('Usa la stessa email dell’account Google');
      return;
    }

    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (!result?.emailVerified) {
        toast.info('Verifica la tua email per accedere');
        navigate(appPath('/verify-email'));
        return;
      }

      await linkPendingCredentialToUser(result.user);
      await refreshUserProfile?.();

      toast.success('Account collegato. Accesso completato');
      navigate(appPath('/dashboard'), { replace: true });
    } catch (error) {
      console.error('Link account login error:', error);
      if (error?.code === 'auth/wrong-password') {
        toast.error('Password non corretta');
      } else if (error?.code === 'auth/user-not-found') {
        toast.error('Utente non trovato');
      } else {
        toast.error('Impossibile collegare l’account');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-br from-muted/30 via-background to-muted/30 text-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
        <div className="p-6 border-b border-border bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-semibold text-gray-900">Collega Google al tuo account</div>
              <div className="text-sm text-gray-600 mt-1">
                Abbiamo trovato un account già esistente con la stessa email. Accedi con password per collegare Google.
              </div>
              {pending?.email ? (
                <div className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700 bg-white/70 border border-gray-200 rounded-xl px-3 py-2">
                  <Mail className="w-4 h-4" />
                  <span className="font-medium">{pending.email}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <input
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                type="email"
                autoComplete="email"
                disabled={Boolean(pending?.email)}
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                {isApp ? 'Su app mobile puoi usare anche redirect.' : 'Su web verrà collegato via popup/redirect.'}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Lock className="w-4 h-4" /> Password
              </label>
              <div className="relative mt-1">
                <input
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 pr-10"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={loading}
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white rounded-xl py-2.5 font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Collegamento...
                </>
              ) : (
                'Accedi e collega'
              )}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" /> Annulla
              </button>
              <Link to={appPath('/login')} className="text-blue-700 hover:underline">
                Vai al login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
